import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { tutorialService, renderTutorialHtml } from '@/services/tutorialService';
import { selectedFacilityAtom } from '@/store/facilityAtom';
import { useContent } from '@/hooks/useContent';
import { useConfig } from '@/hooks/useConfig';
import { SafeHtml } from '@/components/common/SafeHtml';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function QuickTutorialModal({ open, onClose }: Readonly<Props>) {
  const { getContent, getContentString } = useContent();
  const theme          = useTheme();
  const facility = useAtomValue(selectedFacilityAtom);
  const [slide,  setSlide]  = useState(0);

  const { data: slides = [], isLoading: loadingSlides } = useQuery({
    queryKey: ['quick-tutorials', facility?.id],
    queryFn:  () => tutorialService.getQuickTutorials(facility?.id),
    enabled:  open,
    staleTime: 5 * 60_000,
  });

  const { data: downloads = [], isLoading: loadingDownloads } = useQuery({
    queryKey: ['tutorial-downloads', facility?.id],
    queryFn:  () => tutorialService.getDownloads(facility?.id),
    enabled:  open,
    staleTime: 5 * 60_000,
  });

  const slideShowEnabled = useConfig('EnableTutorialSlideShow') === 'Y';
  const rawTimer = Number(useConfig('TutorialSlideTimer'));
  const timerMs = Math.max(1000, Number.isFinite(rawTimer) && rawTimer > 0 ? rawTimer : 10_000);
  const total = slides.length;
  const current = slides[slide];

  useEffect(() => {
    if (!open || !slideShowEnabled || total <= 1) return;
    const id = window.setTimeout(() => {
      setSlide((s) => (s + 1 >= total ? 0 : s + 1));
    }, timerMs);
    return () => window.clearTimeout(id);
  }, [open, slide, slideShowEnabled, timerMs, total]);

  const slideHtml = (s: typeof current) => {
    if (!s) return '';
    if (s.contentKind === 'html' && s.htmlContent) return s.htmlContent;
    if (s.contentKind === 'image' && s.imageUrl)
      return `<img src="${s.imageUrl}" alt="${s.title}" style="max-width:100%;border-radius:4px;" />`;
    return s.renderedDescription ?? renderTutorialHtml(s.description, s.assetBaseUrl);
  };

  // Reset to first slide when reopened.
  const handleClose = () => { setSlide(0); onClose(); };

  const prev = () => setSlide((s) => Math.max(0, s - 1));
  const next = () => setSlide((s) => Math.min(total - 1, s + 1));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          minHeight: 480,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 3,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} color="inherit">
          {getContent('PPQuickTutorialTitle') || 'Quick Tutorial'}
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label={getContentString('PortalClose') || 'Close'} sx={{ color: 'rgba(255,255,255,0.8)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {loadingSlides ? (
          <Box sx={{ p: 4 }}>
            <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1 }} />
          </Box>
        ) : total === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography color="text.secondary">{getContent('PPQuickTutorialNoSlides') || 'No tutorial slides available.'}</Typography>
          </Box>
        ) : (
          <>
            {/* Slide area */}
            <Box sx={{ flex: 1, px: 4, pt: 3, pb: 2, minHeight: 300 }}>
              {/* Slide title */}
              <Typography
                variant="h6"
                fontWeight={500}
                color="primary"
                sx={{ mb: 2, lineHeight: 1.3 }}
              >
                {current?.title}
              </Typography>

              {/* Slide content */}
              <SafeHtml
                html={slideHtml(current)}
                sx={{
                  minHeight: 200,
                  fontSize: 14,
                  lineHeight: 1.7,
                  color: 'text.secondary',
                  '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
                  '& a': { color: 'primary.main' },
                }}
              />
            </Box>

            {/* Slide nav — dots + arrows */}
            {total > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  pb: 2,
                }}
              >
                <IconButton
                  size="small"
                  onClick={prev}
                  disabled={slide === 0}
                  sx={{ color: 'primary.main' }}
                  aria-label={getContentString('PPQuickTutorialPrevSlide')}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>

                {/* Dot indicators */}
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {slides.map((_, i) => (
                    <Box
                      key={i}
                      component="button"
                      onClick={() => setSlide(i)}
                      aria-label={`Slide ${i + 1}`}
                      sx={{
                        width:  i === slide ? 20 : 8,
                        height: 8,
                        borderRadius: 4,
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 250ms cubic-bezier(0.4,0,0.2,1)',
                        background: i === slide
                          ? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.25),
                        p: 0,
                      }}
                    />
                  ))}
                </Box>

                <IconButton
                  size="small"
                  onClick={next}
                  disabled={slide === total - 1}
                  sx={{ color: 'primary.main' }}
                  aria-label={getContentString('PPQuickTutorialNextSlide')}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Slide counter */}
            {total > 1 && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ textAlign: 'center', pb: 1 }}
              >
                {slide + 1} / {total}
              </Typography>
            )}

            {/* Downloads section */}
            {(downloads.length > 0 || loadingDownloads) && (
              <>
                <Divider sx={{ mx: 3 }} />
                <Box sx={{ px: 4, py: 2 }}>
                  <Typography
                    variant="overline"
                    color="primary"
                    fontWeight={600}
                    sx={{ display: 'block', mb: 1.5 }}
                  >
                    {getContent('PPQuickTutorialDownloads') || 'Download'}
                  </Typography>

                  {loadingDownloads ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {downloads.map((dl) => (
                        <Box
                          key={dl.code}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <DownloadIcon
                            fontSize="small"
                            sx={{ color: 'primary.main', flexShrink: 0 }}
                          />
                          <Typography
                            component="a"
                            href={`${dl.assetBaseUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': { color: 'primary.dark' },
                            }}
                          >
                            {dl.title}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={handleClose} variant="outlined">
          {getContent('PortalClose') || 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
